import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, Grow } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessIcon from "@mui/icons-material/Business";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import {
  ThemeScope, Button, Input, Label,
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Alert, Badge, Separator,
} from "./ui/shadcn";

// Design 11 - light "ERP shell" (app bar + icon rail + modal-style sign-in card)
const INK = "#0f172a";
const MUTED = "#475569";
const EASE = [0.22, 1, 0.36, 1];

const THEME_VARS = {
  "--background": "#ffffff",
  "--foreground": INK,
  "--card": "#ffffff",
  "--card-foreground": INK,
  "--primary": "#1d4ed8",
  "--primary-foreground": "#ffffff",
  "--secondary": "#eff6ff",
  "--secondary-foreground": "#1e3a8a",
  "--muted": "#f1f5f9",
  "--muted-foreground": MUTED,
  "--accent": "#eff6ff",
  "--destructive": "#b91c1c",
  "--destructive-foreground": "#ffffff",
  "--border": "#d5dde9",
  "--input": "#8795ab",
  "--ring": "#2563eb",
  "--radius": "0.75rem",
};

const MotionButton = motion.create(Button);
const MotionCard = motion.create(Card);

const CHIPS = ["Multi-company", "GST ready", "Audit trail"];

const CSS = [
  "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');",
  ".login11-root, .login11-root * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }",
  ".login11-root p, .login11-root h1, .login11-root h2, .login11-root h3 { overflow-wrap: anywhere; }",

  /* Page shell */
  ".ld11-page { min-height: 100vh; display: flex; flex-direction: column; background: #e9edf4; overflow-x: hidden; position: relative; width: 100%; }",
  "@supports (min-height: 100dvh) { .ld11-page { min-height: 100dvh; } }",
  ".login11-root .ld11-status { gap: .4rem; white-space: nowrap; }",
  ".ld11-dot { width: 7px; height: 7px; border-radius: 50%; background: #94a3b8; display: inline-block; }",
  ".ld11-dot-on { background: #16a34a; }",

  ".ld11-body { flex: 1 1 auto; display: flex; min-height: 0; min-width: 0; }",
  ".ld11-main { flex: 1 1 auto; min-width: 0; position: relative; display: flex; flex-direction: column; }",
  ".ld11-stage { position: relative; z-index: 2; flex: 1 1 auto; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1.25rem; padding: clamp(1rem, 4vw, 2.5rem) clamp(.75rem, 3vw, 1.5rem) 1.25rem; min-width: 0; }",

  /* Decorative mock app */

  /* Card */
  ".login11-root .ld11-card { width: 100%; max-width: 440px; border: 1px solid #c9d3e2; border-radius: 16px; background: #fff; box-shadow: 0 1px 2px rgba(15,23,42,.06), 0 24px 48px -20px rgba(15,23,42,.35); overflow: hidden; position: relative; min-width: 0; }",
  ".ld11-accent { height: 3px; background: #2563eb; }",
  ".ld11-titlebar { display: flex; align-items: center; justify-content: space-between; gap: .75rem; padding: .6rem clamp(1rem, 4vw, 1.5rem); background: #f8fafc; border-bottom: 1px solid #e2e8f0; min-width: 0; }",
  ".ld11-titlebar-r { font-size: .72rem; font-weight: 600; color: #64748b; white-space: nowrap; }",
  ".login11-root .ld11-head { flex-direction: row; align-items: center; gap: .9rem; padding: clamp(1.1rem, 4vw, 1.5rem) clamp(1rem, 4vw, 1.5rem) 1rem; min-width: 0; }",
  ".ld11-tile { width: 46px; height: 46px; border-radius: 12px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: #dbeafe; color: #1d4ed8; }",
  ".ld11-headtext { min-width: 0; display: flex; flex-direction: column; gap: .3rem; }",
  ".login11-root .ld11-title { font-size: clamp(1.3rem, 5vw, 1.55rem); font-weight: 700; letter-spacing: -.4px; line-height: 1.15; color: " + INK + "; }",
  ".login11-root .ld11-desc { font-size: .86rem; line-height: 1.45; color: " + MUTED + "; }",
  ".login11-root .ld11-card .ui-card-content { padding: 0 clamp(1rem, 4vw, 1.5rem) 1rem; }",
  ".login11-root .ld11-card .ui-card-footer { padding: 0 clamp(1rem, 4vw, 1.5rem) 1.4rem; justify-content: center; text-align: center; font-size: .875rem; color: " + MUTED + "; }",

  /* Fields */
  ".ld11-group { margin-bottom: .1rem; min-width: 0; }",
  ".login11-root .ld11-label { color: " + INK + "; font-weight: 600; font-size: .84rem; margin-bottom: .45rem; line-height: 1.2; }",
  ".ld11-labelrow { display: flex; align-items: center; justify-content: space-between; gap: .75rem; margin-bottom: .45rem; flex-wrap: wrap; }",
  ".ld11-labelrow .ld11-label { margin-bottom: 0; }",
  ".ld11-field { position: relative; width: 100%; }",
  ".ld11-field::after { content: ''; position: absolute; inset: -1px; border-radius: 11px; box-shadow: 0 0 0 4px rgba(37,99,235,.22); opacity: 0; transition: opacity .15s ease; pointer-events: none; z-index: 2; }",
  ".ld11-field:focus-within::after { opacity: 1; }",
  ".login11-root .ld11-field .ui-input { height: 3rem; min-height: 44px; border-radius: 10px; background: #f1f5f9; border: 1.5px solid #8795ab; color: " + INK + "; -webkit-text-fill-color: " + INK + "; caret-color: #1d4ed8; opacity: 1; padding: 0 2.9rem 0 2.8rem; font-size: 1rem; font-weight: 500; box-shadow: none; transition: none; }",
  ".login11-root .ld11-field .ui-input.ld11-pw { padding-right: 5.2rem; }",
  ".login11-root .ld11-field .ui-input::placeholder { color: #5f6f86; -webkit-text-fill-color: #5f6f86; opacity: 1; }",
  ".login11-root .ld11-field .ui-input:hover { border-color: #3b6fd8; }",
  ".login11-root .ld11-field .ui-input:focus, .login11-root .ld11-field .ui-input:focus-visible { background: #fff; border-color: #1d4ed8; box-shadow: none; }",
  ".login11-root .ld11-field .ui-input:-webkit-autofill, .login11-root .ld11-field .ui-input:-webkit-autofill:hover, .login11-root .ld11-field .ui-input:-webkit-autofill:active { -webkit-text-fill-color: " + INK + " !important; caret-color: " + INK + "; -webkit-box-shadow: 0 0 0 100px #eef2f7 inset !important; box-shadow: 0 0 0 100px #eef2f7 inset !important; transition: background-color 9999s ease-out 0s; }",
  ".login11-root .ld11-field .ui-input:-webkit-autofill:focus { -webkit-text-fill-color: " + INK + " !important; -webkit-box-shadow: 0 0 0 100px #ffffff inset !important; box-shadow: 0 0 0 100px #ffffff inset !important; }",
  ".login11-root .ld11-field .ui-input:autofill { color: " + INK + "; background-color: #eef2f7; }",
  ".ld11-field input::-ms-reveal, .ld11-field input::-ms-clear { display: none; }",
  ".ld11-fieldicon { position: absolute; left: .9rem; top: 50%; margin-top: -11px; color: #64748b; pointer-events: none; z-index: 1; }",
  ".ld11-field:focus-within .ld11-fieldicon { color: #1d4ed8; }",
  ".ld11-capsicon { position: absolute; right: .9rem; top: 50%; margin-top: -10px; pointer-events: none; z-index: 1; }",
  ".ld11-capsicon-pw { right: 3.4rem; }",
  ".login11-root .ld11-toggle { position: absolute; right: .25rem; top: 50%; margin-top: -1.375rem; height: 2.75rem; width: 2.75rem; color: #475569; border-radius: 10px; z-index: 3; }",
  ".login11-root .ld11-toggle:hover { color: #1d4ed8; background: rgba(37,99,235,.1); }",
  ".ld11-slot { min-height: 1.4rem; padding-top: .3rem; }",
  ".ld11-caps { display: flex; align-items: center; gap: .35rem; color: #92400e; font-size: 12px; font-weight: 500; }",
  ".login11-root .ld11-link { color: #1d4ed8; text-decoration: none; font-weight: 600; font-size: .85rem; border-radius: 4px; padding: .35rem 0; display: inline-block; }",
  ".login11-root .ld11-link:hover { text-decoration: underline; text-underline-offset: 3px; }",
  ".login11-root .ld11-link:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }",
  ".login11-root .ld11-alert { display: flex; align-items: flex-start; gap: .6rem; background: #fef2f2; border-color: #fca5a5; color: #b91c1c; font-weight: 500; font-size: .84rem; margin-bottom: 1rem; border-radius: 10px; min-width: 0; }",

  /* Buttons */
  ".login11-root .ld11-primary { width: 100%; height: 3rem; min-height: 44px; border-radius: 10px; font-size: 1rem; font-weight: 600; color: #fff; background: #1d4ed8; box-shadow: 0 8px 16px -8px rgba(29,78,216,.6); transition: opacity .15s ease; }",
  ".login11-root .ld11-primary:hover { background: #1e40af; opacity: 1; }",
  ".login11-root .ld11-primary:disabled { opacity: .7; box-shadow: none; }",
  ".ld11-primary svg { transition: transform .2s ease; }",
  ".ld11-primary:hover svg { transform: translateX(3px); }",
  ".login11-root .ld11-bio { width: 100%; height: 3rem; min-height: 44px; margin-top: .75rem; border-radius: 10px; font-weight: 600; color: #1d4ed8; border-color: #93b4f0; }",

  /* Company cards */
  ".ld11-companylist { display: flex; flex-direction: column; gap: .6rem; max-height: min(288px, 46vh); overflow-y: auto; overflow-x: hidden; padding: 4px; margin: 0 -4px 1rem; }",
  ".login11-root .ld11-company { position: relative; display: flex; align-items: center; gap: .85rem; padding: .7rem .95rem; min-height: 56px; border-radius: 12px; border: 1.5px solid #c3cddc; background: #fff; cursor: pointer; outline: none; box-shadow: none; min-width: 0; flex-shrink: 0; }",
  ".login11-root .ld11-company:hover { border-color: #60a5fa; }",
  ".login11-root .ld11-company:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }",
  ".ld11-ring { position: absolute; inset: -1.5px; border-radius: 12px; border: 2px solid #2563eb; background: rgba(37,99,235,.07); pointer-events: none; z-index: 0; }",
  ".ld11-avatarc { position: relative; z-index: 1; width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-weight: 700; color: #1e40af; background: #dbeafe; }",
  ".ld11-company[aria-checked='true'] .ld11-avatarc { color: #fff; background: #1d4ed8; }",
  ".ld11-companyname { position: relative; z-index: 1; flex: 1; min-width: 0; font-weight: 600; font-size: .94rem; color: " + INK + "; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }",
  ".ld11-radio { position: relative; z-index: 1; width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border: 2px solid #8795ab; background: #fff; }",
  ".ld11-company[aria-checked='true'] .ld11-radio { border-color: #1d4ed8; background: #1d4ed8; color: #fff; }",

  ".ld11-foot { margin: 0; font-size: .75rem; color: #475569; text-align: center; display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: .2rem; }",

  /* Session dialog */
  ".ld11-dhead { display: flex; align-items: center; gap: .85rem; padding: 1.25rem 1.25rem 1rem; border-bottom: 1px solid #e2e8f0; }",
  ".ld11-dtile { width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: #fff7ed; color: #c2410c; }",
  ".ld11-dtitle { margin: 0; font-size: 1.05rem; font-weight: 700; color: " + INK + "; line-height: 1.3; }",
  ".ld11-dsub { margin: .2rem 0 0; font-size: .78rem; color: " + MUTED + "; }",
  ".login11-root .ld11-dnote { margin: 1rem 1.25rem 0; background: #eff6ff; border-color: #bfdbfe; color: #1e3a8a; font-size: .82rem; line-height: 1.5; }",
  ".ld11-dlist { padding: 1rem 1.25rem .25rem; }",
  ".ld11-dlabel { margin: 0 0 .7rem; font-size: .72rem; font-weight: 700; color: #475569; letter-spacing: .8px; text-transform: uppercase; }",
  ".ld11-session { display: flex; align-items: center; gap: .8rem; padding: .8rem; margin-bottom: .6rem; border-radius: 12px; border: 1px solid #c9d3e2; background: #f8fafc; min-width: 0; }",
  ".ld11-sicon { width: 40px; height: 40px; border-radius: 10px; background: #dbeafe; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }",
  ".ld11-sinfo { flex: 1; min-width: 0; }",
  ".ld11-sname { margin: 0; font-weight: 600; font-size: .87rem; color: " + INK + "; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }",
  ".ld11-smeta { display: flex; align-items: center; gap: .35rem; margin-top: .25rem; flex-wrap: wrap; font-size: .73rem; color: " + MUTED + "; }",
  ".login11-root .ld11-signout { min-width: 84px; min-height: 44px; flex-shrink: 0; font-size: .78rem; font-weight: 600; }",
  ".ld11-dfoot { padding: .5rem 1.25rem 1.25rem; }",
  ".login11-root .ld11-cancel { width: 100%; min-height: 44px; font-weight: 600; }",

  ".ld11-logo { display: flex; justify-content: center; }",
  ".ld11-logo img { height: clamp(30px, 6vw, 38px); width: auto; display: block; }",
  ".ld11-hero { text-align: center; max-width: 440px; }",
  ".ld11-hero h2 { margin: 0 0 .4rem; font-size: clamp(1.1rem, 4vw, 1.35rem); font-weight: 700; letter-spacing: -.3px; color: " + INK + "; }",
  ".ld11-hero p { margin: 0; font-size: .9rem; line-height: 1.5; color: " + MUTED + "; }",
  ".ld11-chips { display: flex; flex-wrap: wrap; justify-content: center; gap: .5rem; }",
  ".login11-root .ld11-chip { gap: .35rem; background: #fff; }",
  ".ld11-stage { gap: clamp(1rem, 3vw, 1.5rem) !important; padding-top: clamp(1.5rem, 6vh, 3rem) !important; padding-bottom: clamp(1.25rem, 4vh, 2rem) !important; }",
  "@media (max-height: 480px) { .ld11-stage { justify-content: flex-start; } .ld11-hero p { display: none; } }",
  /* Responsive */
  "@media (prefers-reduced-motion: reduce) { .login11-root, .login11-root *, .login11-root *::before, .login11-root *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; } }",
].join("\n");

function LoginDesign11({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();
  const reduce = useReducedMotion();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // ── Visual helpers (styling / motion only) ─────────────────────────────────
  // Self-contained entrance props (no variant chain). rise = fade+move, slide = move only (used for inputs).
  const rise = (delay = 0, y = 14) =>
    reduce
      ? { initial: false }
      : { initial: { opacity: 1, y }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, delay, ease: EASE } };
  const slide = (delay = 0, y = 12) =>
    reduce
      ? { initial: false }
      : { initial: { y }, animate: { y: 0 }, transition: { duration: 0.5, delay, ease: EASE } };

  const handleCompanyKeyDown = (e, idx) => {
    const key = e.key;
    if (key === " " || key === "Enter") {
      e.preventDefault();
      setSelectedCompany(companies[idx]);
      return;
    }
    let next = null;
    if (key === "ArrowDown" || key === "ArrowRight") next = (idx + 1) % companies.length;
    if (key === "ArrowUp" || key === "ArrowLeft") next = (idx - 1 + companies.length) % companies.length;
    if (next === null) return;
    e.preventDefault();
    setSelectedCompany(companies[next]);
    const el = document.getElementById("ld11-company-" + next);
    if (el) el.focus();
  };

  const capsHelper = (
    <div className="ld11-slot">
      <AnimatePresence initial={false}>
        {capsLockOn && (
          <motion.div
            key="caps"
            role="status"
            className="ld11-caps"
            initial={{ opacity: 1, y: reduce ? 0 : -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0.1 : 0.18 }}
          >
            <WarningAmberIcon sx={{ fontSize: 14, color: "#d97706" }} />Caps Lock is ON
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const errorAlert = (
    <AnimatePresence initial={false}>
      {errorMessage && (
        <motion.div
          key="err"
          initial={{ opacity: 1, y: reduce ? 0 : -8 }}
          animate={{ opacity: 1, y: 0, x: reduce ? 0 : [0, -7, 7, -4, 4, 0] }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.15 : 0.45 }}
        >
          <Alert variant="destructive" className="ld11-alert">
            <WarningAmberIcon sx={{ fontSize: 18, flexShrink: 0, mt: "1px" }} />
            <span style={{ minWidth: 0 }}>{errorMessage}</span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const viewMotion = {
    initial: reduce ? false : { y: 10 },
    animate: { y: 0 },
    exit: { opacity: 0, y: reduce ? 0 : -8, transition: { duration: 0.15 } },
    transition: { duration: 0.35, ease: EASE },
  };

  return (
    <ThemeScope vars={THEME_VARS} className="login11-root ld11-page">
      <style>{CSS}</style>

      <div className="ld11-body">
        <main className="ld11-main">
          <div className="ld11-stage">
            <motion.div className="ld11-logo" {...rise(0.05, -10)}>
              <img src="/RBLOGOBLUE.svg" alt="ReonBook" />
            </motion.div>
            <motion.div className="ld11-hero" {...rise(0.1, 8)}>
              <h2>Accounting that keeps up with your business</h2>
              <p>Invoices, GST and multi-company ledgers in one place.</p>
            </motion.div>

            <MotionCard
              className="ld11-card"
              initial={reduce ? false : { opacity: 1, y: 28, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.12, ease: EASE }}
            >
              <div className="ld11-accent" aria-hidden="true" />
              <div className="ld11-titlebar">
                <Badge variant="outline" className="ld11-status">
                  <span className={"ld11-dot" + (isCompanySelectionVisible ? " ld11-dot-on" : "")} />
                  {isCompanySelectionVisible ? "Signed in" : "Authentication required"}
                </Badge>
                <span className="ld11-titlebar-r">ReonBook ERP</span>
              </div>

              <AnimatePresence mode="wait" initial>
                {!isCompanySelectionVisible ? (

                  /* ── LOGIN FORM ── */
                  <motion.div key="login" {...viewMotion}>
                    <CardHeader className="ld11-head">
                      <motion.div {...rise(0.2)} className="ld11-tile" aria-hidden="true">
                        <LockOutlinedIcon sx={{ fontSize: 24 }} />
                      </motion.div>
                      <motion.div {...rise(0.24)} className="ld11-headtext">
                        <CardTitle role="heading" aria-level={1} className="ld11-title">Welcome</CardTitle>
                        <CardDescription className="ld11-desc">Sign in to access your ReonBook dashboard</CardDescription>
                      </motion.div>
                    </CardHeader>

                    <CardContent>
                      <form onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>

                        <motion.div {...slide(0.28)} className="ld11-group">
                          <Label htmlFor="ld11-identifier" className="ld11-label">Username, Email or Mobile</Label>
                          <div className="ld11-field">
                            <PersonOutlineIcon className="ld11-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                            <Input
                              id="ld11-identifier"
                              type="text"
                              autoComplete="username"
                              placeholder="Username, email or mobile number"
                              value={identifier}
                              onChange={(e) => setIdentifier(e.target.value)}
                              onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                            />
                            {capsLockOn && <WarningAmberIcon className="ld11-capsicon" sx={{ color: "#d97706", fontSize: 20 }} aria-hidden="true" />}
                          </div>
                          {capsHelper}
                        </motion.div>

                        <motion.div {...slide(0.34)} className="ld11-group">
                          <div className="ld11-labelrow">
                            <Label htmlFor="ld11-password" className="ld11-label">Password</Label>
                            <Link to="/forgot-password" className="ld11-link">Forgot password?</Link>
                          </div>
                          <div className="ld11-field">
                            <LockOutlinedIcon className="ld11-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                            <Input
                              id="ld11-password"
                              className="ld11-pw"
                              type={showPassword ? "text" : "password"}
                              autoComplete="current-password"
                              placeholder="Enter your password"
                              value={userPassword}
                              onChange={(e) => setUserPassword(e.target.value)}
                              onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                            />
                            {capsLockOn && <WarningAmberIcon className="ld11-capsicon ld11-capsicon-pw" sx={{ color: "#d97706", fontSize: 20 }} aria-hidden="true" />}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="ld11-toggle"
                              onClick={handleTogglePasswordVisibility}
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              aria-pressed={showPassword}
                            >
                              {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                            </Button>
                          </div>
                          {capsHelper}
                        </motion.div>

                        <motion.div {...rise(0.4)} style={{ marginTop: ".6rem" }}>
                          {errorAlert}

                          <MotionButton
                            type="submit"
                            size="lg"
                            disabled={isLoading}
                            aria-busy={isLoading}
                            className="ld11-primary"
                            whileHover={reduce || isLoading ? undefined : { y: -1 }}
                            whileTap={reduce || isLoading ? undefined : { scale: 0.98 }}
                            transition={{ type: "spring", stiffness: 400, damping: 24 }}
                          >
                            {isLoading
                              ? <><CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />Signing in...</>
                              : <>Sign In<ArrowForwardIcon sx={{ fontSize: 20 }} /></>}
                          </MotionButton>

                          {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                            <Button
                              type="button"
                              variant="outline"
                              className="ld11-bio"
                              onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}>
                              Login with Biometric
                            </Button>
                          )}
                        </motion.div>
                      </form>

                      <Separator style={{ margin: "1.4rem 0 0" }} />
                    </CardContent>

                    <CardFooter>
                      <motion.p {...rise(0.46, 8)} style={{ margin: 0 }}>
                        Don't have an account?{" "}
                        <Link to="/register" className="ld11-link" style={{ fontSize: "inherit", fontWeight: 700 }}>
                          Sign up now
                        </Link>
                      </motion.p>
                    </CardFooter>
                  </motion.div>

                ) : (

                  /* ── COMPANY SELECTION ── */
                  <motion.div key="company" {...viewMotion}>
                    <CardHeader className="ld11-head">
                      <motion.div {...rise(0.05)} className="ld11-tile" aria-hidden="true">
                        <BusinessIcon sx={{ fontSize: 24 }} />
                      </motion.div>
                      <motion.div {...rise(0.09)} className="ld11-headtext">
                        <CardTitle role="heading" aria-level={1} className="ld11-title">Select Organization</CardTitle>
                        <CardDescription className="ld11-desc" id="ld11-company-desc">Choose which organization to access</CardDescription>
                      </motion.div>
                    </CardHeader>

                    <CardContent>
                      <div
                        className="ld11-companylist"
                        role="radiogroup"
                        aria-label="Choose your organization"
                        aria-describedby="ld11-company-desc"
                      >
                        {companies.map((c, idx) => {
                          const isSel = selectedCompany?.companyId === c.companyId;
                          const firstFocusable = !selectedCompany && idx === 0;
                          return (
                            <MotionCard
                              key={c.companyId}
                              id={"ld11-company-" + idx}
                              role="radio"
                              aria-checked={isSel}
                              tabIndex={isSel || firstFocusable ? 0 : -1}
                              className="ld11-company"
                              onClick={() => setSelectedCompany(companies.find((x) => x.companyId === c.companyId))}
                              onKeyDown={(e) => handleCompanyKeyDown(e, idx)}
                              {...(reduce ? { initial: false } : { initial: { y: 10 }, animate: { y: 0 } })}
                              whileHover={reduce ? undefined : { y: -2 }}
                              whileTap={reduce ? undefined : { scale: 0.99 }}
                              transition={{ type: "spring", stiffness: 380, damping: 26, delay: reduce ? 0 : Math.min(idx, 6) * 0.04 }}
                            >
                              <motion.span
                                className="ld11-ring"
                                aria-hidden="true"
                                initial={false}
                                animate={{ opacity: isSel ? 1 : 0 }}
                                transition={{ duration: reduce ? 0.05 : 0.18 }}
                              />
                              <span className="ld11-avatarc" aria-hidden="true">{(c.companyName || "?").trim().charAt(0).toUpperCase()}</span>
                              <span className="ld11-companyname">{c.companyName}</span>
                              <span className="ld11-radio" aria-hidden="true">
                                <AnimatePresence initial={false}>
                                  {isSel && (
                                    <motion.span
                                      key="tick"
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                      exit={{ scale: 0 }}
                                      transition={{ duration: reduce ? 0.05 : 0.18 }}
                                      style={{ display: "flex" }}
                                    >
                                      <CheckIcon sx={{ fontSize: 14 }} />
                                    </motion.span>
                                  )}
                                </AnimatePresence>
                              </span>
                            </MotionCard>
                          );
                        })}
                      </div>

                      {errorAlert}

                      <MotionButton
                        type="button"
                        size="lg"
                        className="ld11-primary"
                        onClick={() => FN_SelectCompany()}
                        whileHover={reduce ? undefined : { y: -1 }}
                        whileTap={reduce ? undefined : { scale: 0.98 }}
                        transition={{ type: "spring", stiffness: 400, damping: 24 }}
                      >
                        Continue to Dashboard<ArrowForwardIcon sx={{ fontSize: 20 }} />
                      </MotionButton>
                    </CardContent>
                  </motion.div>
                )}
              </AnimatePresence>
            </MotionCard>

            <motion.div className="ld11-chips" {...rise(0.6, 6)}>
              {CHIPS.map((c) => (
                <Badge key={c} variant="outline" className="ld11-chip"><CheckIcon sx={{ fontSize: 12, color: "#16a34a" }} />{c}</Badge>
              ))}
            </motion.div>

            <motion.p className="ld11-foot" {...rise(0.7, 0)}>
              <CopyrightIcon sx={{ fontSize: 12 }} />
              <span>{new Date().getFullYear()} Powered by Soltrack Technologies Pvt Ltd</span>
            </motion.p>
          </div>
        </main>
      </div>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="login11-root"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="ld11-session-title"
        slots={{ transition: Grow }}
        transitionDuration={{ enter: 320, exit: 180 }}
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(15,23,42,0.5)" } },
          paper: { sx: { borderRadius: "16px", overflow: "hidden", m: 2, width: "calc(100% - 32px)", maxHeight: "calc(100% - 32px)" } },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={THEME_VARS} className="login11-root">
            <div className="ld11-accent" aria-hidden="true" />
            <div className="ld11-dhead">
              <span className="ld11-dtile"><DevicesIcon sx={{ fontSize: 22 }} /></span>
              <div style={{ minWidth: 0 }}>
                <h2 id="ld11-session-title" className="ld11-dtitle">Session Limit Reached</h2>
                <p className="ld11-dsub">
                  {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                </p>
              </div>
            </div>
            <Alert className="ld11-dnote">Sign out of one of the devices below to log in on this device.</Alert>

            <div className="ld11-dlist">
              <p className="ld11-dlabel">Active Sessions</p>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <motion.div
                  key={session.id}
                  className="ld11-session"
                  initial={reduce ? false : { opacity: 1, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: reduce ? 0 : 0.08 + idx * 0.06, ease: EASE }}
                  whileHover={reduce ? undefined : { y: -1 }}
                >
                  <span className="ld11-sicon">{deviceIcon(session.device_type)}</span>
                  <div className="ld11-sinfo">
                    <p className="ld11-sname">{session.device_name || "Unknown Device"}</p>
                    <div className="ld11-smeta">
                      <AccessTimeIcon sx={{ fontSize: 12 }} />
                      <span>{timeAgo(session.last_activity)}</span>
                      {session.ip_address && <span>· {session.ip_address}</span>}
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="ld11-signout"
                    disabled={kickingSessionId === session.id}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                      : "Sign Out"}
                  </Button>
                </motion.div>
              ))}
            </div>

            <div className="ld11-dfoot">
              <Button type="button" variant="outline" className="ld11-cancel" onClick={() => setSessionPickerData(null)}>
                Cancel
              </Button>
            </div>
          </ThemeScope>
        </DialogContent>
      </Dialog>
    </ThemeScope>
  );
}

export default LoginDesign11;
